import type {
  IntervieweeProfile,
  MassGraveIncident,
  MissingPersonIncident,
  MurderedPersonIncident,
  TortureOrRapeIncident,
} from "../../../domain/contracts";

export type IncidentFamily =
  | "tortureOrRape"
  | "murdered"
  | "missingOrAbducted"
  | "massGrave";

export type IncidentChartMode = "bar" | "pie";

export type IncidentFamilyCounts = Record<IncidentFamily, number>;

export type IncidentDetailCardRecord =
  | {
      family: "tortureOrRape";
      index: number;
      incident: TortureOrRapeIncident;
    }
  | {
      family: "murdered";
      index: number;
      incident: MurderedPersonIncident;
    }
  | {
      family: "missingOrAbducted";
      index: number;
      incident: MissingPersonIncident;
    }
  | {
      family: "massGrave";
      index: number;
      incident: MassGraveIncident;
    };

function countItems<T>(items: T[] | null | undefined): number {
  return items?.length ?? 0;
}

export function buildIncidentFamilyCounts(profile: IntervieweeProfile | null | undefined): IncidentFamilyCounts {
  return {
    tortureOrRape: countItems(profile?.tortureOrRapeIncidents),
    murdered: countItems(profile?.murderedPersons),
    missingOrAbducted: countItems(profile?.missingPersons),
    massGrave: countItems(profile?.massGraves),
  };
}

export function hasIncidentDetails(profile: IntervieweeProfile | null | undefined): boolean {
  const counts = buildIncidentFamilyCounts(profile);
  return Object.values(counts).some((count) => count > 0);
}

export function flattenIncidentCards(profile: IntervieweeProfile | null | undefined): IncidentDetailCardRecord[] {
  if (!profile) {
    return [];
  }

  const tortureCards: IncidentDetailCardRecord[] = (profile.tortureOrRapeIncidents ?? []).map((incident, index) => ({
    family: "tortureOrRape",
    index,
    incident,
  }));

  const murderedCards: IncidentDetailCardRecord[] = (profile.murderedPersons ?? []).map((incident, index) => ({
    family: "murdered",
    index,
    incident,
  }));

  const missingCards: IncidentDetailCardRecord[] = (profile.missingPersons ?? []).map((incident, index) => ({
    family: "missingOrAbducted",
    index,
    incident,
  }));

  const massGraveCards: IncidentDetailCardRecord[] = (profile.massGraves ?? []).map((incident, index) => ({
    family: "massGrave",
    index,
    incident,
  }));

  return [...tortureCards, ...murderedCards, ...missingCards, ...massGraveCards];
}
